<script setup lang="ts">
import { defineProps, defineEmits, computed } from "vue";
import DiamondsSearchFormIcons from "@/components/diamonds/form/diamondsSearchFormIcons.vue";
import DiamondsSearchFormNumberRange from "@/components/diamonds/form/diamondsSearchFormNumberRange.vue";
import DiamondsSearchFormRange from "@/components/diamonds/form/diamondsSearchFormRange.vue";
import DiamondsSearchFormSwitch from "@/components/diamonds/form/diamondsSearchFormSwitch.vue";
import DiamondsSearchFormList from "@/components/diamonds/form/diamondsSearchFormList.vue";
import DiamondsSearchFormPresets from "@/components/diamonds/form/diamondsSearchFormPresets.vue";

const props = withDefaults(defineProps<{
  filters: any;
  activeFilters: any;
  simpleFilterKeys?: string[];
  getFilterLabel: (key: string) => string;
  getActiveValueLabel: (key: string, value: any) => string;
  isFilterDefined: (key: string) => boolean;
  presets?: any[];
}>(), {
  simpleFilterKeys: () => ['type', 'size', 'form'],
  presets: () => []
});

const emit = defineEmits<{
  (e: "updateFilter", key: string, value: any): void;
  (e: "updateFilters", value: any): void;
}>();

const visibleFilterKeys = computed(() => {
  return props.simpleFilterKeys
    .filter(key => props.filters[key])
    .sort((a, b) => (props.filters[a].order || 0) - (props.filters[b].order || 0));
});

const updateFilter = (key: string, val: any) => {
  emit('updateFilter', key, val);
}

const sizeSteps = [0.3, 0.4, 0.5, 0.7, 1.0, 1.5, 2.0, 2.5, 3.0];
const filteredSizeSteps = computed(() => {
  const min = props.filters.size?.min ?? 0;
  const max = props.filters.size?.max ?? 10;
  return sizeSteps.filter(s => s >= min && s <= max);
});

const getDiamondImageUrl = (form: string) => {
  const formFilter = props.filters['form'];
  if (!formFilter || !formFilter.image) return "";
  const processedVal = formFilter.lowercaseImage !== false ? form.toLowerCase() : form;
  return formFilter.image.replace("{value}", processedVal);
};

const activeForm = computed(() => {
  return props.activeFilters['form'] || props.filters['form']?.default || 'BR';
});

</script>

<template>
  <div class="diamonds-search-form-simple">
    <template v-for="key in visibleFilterKeys" :key="key">
      <div class="filter-row border-b border-gray-200 py-4 last:border-b-0">
        <div class="mt-2 overflow-x-auto no-scrollbar scroll-smooth">
           <template v-if="filters[key].type === 'icon' && filters[key].view === 'list'">
            <DiamondsSearchFormIcons
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'number' && filters[key].view === 'range' && key !== 'size'">
            <DiamondsSearchFormNumberRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="key === 'size'">
            <div class="mt-6 overflow-x-auto no-scrollbar scroll-smooth">
              <div class="flex gap-8 pb-4 px-2 h-32">
                <div
                  v-for="s in filteredSizeSteps"
                  :key="s"
                  class="flex flex-col justify-center items-center cursor-pointer min-w-fit"
                  @click="updateFilter(key, {min: s, max: s})"
                >
                  <div
                    class="w-16 h-16 overflow-hidden rounded-full flex items-center justify-center mb-2 transition-all duration-300 relative bg-white"
                    :class="activeFilters[key] && activeFilters[key].min === s && activeFilters[key].max === s
                      ? 'shadow-md w-20 h-20'
                      : ''"
                  >
                    <div
                      class="transition-transform duration-500 ease-out"
                      :style="{ transform: `scale(${0.4 + (s / 3) * 0.6})` }"
                    >
                      <img
                        :src="getDiamondImageUrl(activeForm)"
                        :alt="activeForm"
                        class="w-18 h-18 object-contain opacity-80"
                      />
                    </div>
                  </div>
                  <span
                    class="text-sm text-gray-500 tracking-wide whitespace-nowrap transition-all duration-300"
                    :class="activeFilters[key] && activeFilters[key].min === s && activeFilters[key].max === s ? 'font-bold' : ''"
                  >
                    {{ s.toLocaleString('de-DE', { minimumFractionDigits: 1 }) }} ct.
                  </span>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="(filters[key].type === 'icon' || filters[key].type === 'text') && filters[key].view === 'range'">
            <DiamondsSearchFormRange
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="(filters[key].type === 'text' || filters[key].type === 'bool') && filters[key].view === 'switch'">
            <DiamondsSearchFormSwitch
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
          <template v-else-if="filters[key].type === 'text' && filters[key].view === 'list'">
            <DiamondsSearchFormList
              :filter="filters[key]"
              :active-value="activeFilters[key]"
              @update="(val) => updateFilter(key, val)"
            />
          </template>
        </div>
      </div>
      <DiamondsSearchFormPresets
        v-if="key === 'type' && presets && presets.length > 0"
        :presets="presets"
        :active-filters="activeFilters"
        class="mb-6 mt-2"
        @update-filters="(val) => emit('updateFilters', val)"
      />
    </template>
  </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>